import {
	Directive,
	ElementRef,
	HostListener,
	Input,
	signal,
	OnDestroy,
	OnInit
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Directive({
	selector: '[appDateMask]',
	standalone: true
})
export class DateMaskDirective implements OnInit, OnDestroy {
	@Input() dateFormat: string = 'mm/dd/yyyy';
	@Input() enableMask: boolean = true;

	private previousValue = signal('');
	private inputSubject = new Subject<string>();
	private subscription: Subscription | null = null;

	constructor(private el: ElementRef<HTMLInputElement>) {}

	ngOnInit() {
		this.subscription = this.inputSubject
			.pipe(debounceTime(1000), distinctUntilChanged())
			.subscribe(() => {
				const input = this.el.nativeElement;
				if (document.activeElement === input) {
					this.autoPadMonthAndDay(input);
					this.autoCompleteYear(input);
				}
			});
	}

	ngOnDestroy() {
		this.subscription?.unsubscribe();
	}

	@HostListener('input', ['$event'])
	onInput(event: Event): void {
		if (!this.enableMask) return;

		const input = this.el.nativeElement;
		let value = input.value;
		const cursorPosition = input.selectionStart || 0;
		const previousValue = this.previousValue();

		// Handle empty value - clear completely
		if (!value || value.trim() === '') {
			input.value = '';
			this.previousValue.set('');
			return;
		}

		// Detect if this was a deletion operation
		const isDeletion = value.length < previousValue.length;

		// Get separator from format
		const separator = this.dateFormat.includes('/') ? '/' : '-';

		// Apply mask based on format
		const { masked, isAutoPadded } =
			this.tryApplyStructurePreservingMask(
				value,
				separator,
				isDeletion
			) || this.applyMask(value, separator, false, isDeletion);

		// Update input value
		if (masked !== value) {
			input.value = masked;

			// Adjust cursor position
			const newPosition = this.getNewCursorPosition(
				previousValue,
				masked,
				cursorPosition,
				separator,
				isAutoPadded,
				isDeletion
			);
			input.setSelectionRange(newPosition, newPosition);
		}

		this.previousValue.set(masked);

		// Trigger debounce for auto-completion
		this.inputSubject.next(masked);
	}

	@HostListener('blur', ['$event'])
	onBlur(event: Event): void {
		if (!this.enableMask) return;

		const input = this.el.nativeElement;
		let value = input.value.trim();

		if (!value) return;

		const separator = this.dateFormat.includes('/') ? '/' : '-';

		// Apply mask with force padding (for blur, we want to complete incomplete fields)
		const { masked } = this.applyMask(value, separator, true, false);

		if (masked !== value) {
			input.value = masked;
			this.previousValue.set(masked);
		}

		// Also try auto-complete on blur (immediate)
		this.autoCompleteYear(input);
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent): void {
		if (!this.enableMask) return;

		const input = this.el.nativeElement;
		const key = event.key;
		const cursorPosition = input.selectionStart || 0;
		const value = input.value;

		// Handle backspace at separator position
		if (key === 'Backspace') {
			const separator = this.dateFormat.includes('/') ? '/' : '-';
			// If cursor is right after a separator, delete the separator and the character before it
			if (cursorPosition > 0 && value[cursorPosition - 1] === separator) {
				event.preventDefault();
				const newValue =
					value.slice(0, cursorPosition - 2) +
					value.slice(cursorPosition);
				input.value = newValue;
				input.setSelectionRange(cursorPosition - 2, cursorPosition - 2);
				// Trigger input event to reapply mask
				input.dispatchEvent(new Event('input', { bubbles: true }));
				return;
			}
		}

		// Allow: backspace, delete, tab, escape, enter, arrows
		if (
			[
				'Backspace',
				'Delete',
				'Tab',
				'Escape',
				'Enter',
				'ArrowLeft',
				'ArrowRight',
				'Home',
				'End'
			].includes(key)
		) {
			return;
		}

		// Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
		if (
			(event.ctrlKey || event.metaKey) &&
			['a', 'c', 'v', 'x'].includes(key.toLowerCase())
		) {
			return;
		}

		// Get separator from format
		const separator = this.dateFormat.includes('/') ? '/' : '-';

		// Only allow numbers and separator
		if (!/^\d$/.test(key) && key !== separator) {
			event.preventDefault();
			return;
		}

		// Check if we're at max length
		const maxLength = this.getMaxLength();
		if (
			value.length >= maxLength &&
			input.selectionStart === input.selectionEnd
		) {
			event.preventDefault();
		}
	}

	private applyMask(
		value: string,
		separator: string,
		forcePad: boolean = false,
		isDeletion: boolean = false
	): { masked: string; isAutoPadded: boolean } {
		const numbers = value.replace(/[^\d]/g, '');
		const digitIndices: number[] = [];
		for (let i = 0; i < value.length; i++) {
			if (/\d/.test(value[i])) {
				digitIndices.push(i);
			}
		}

		if (!numbers) return { masked: '', isAutoPadded: false };

		const formatParts = this.getFormatParts();
		let masked = '';
		let numberIndex = 0;
		let isAutoPadded = false;

		let monthStr = '';
		let yearStr = '';
		let tempIndex = 0;
		for (const part of formatParts) {
			if (tempIndex >= numbers.length) break;
			const partLength = part.length;
			let partValue = numbers.substr(tempIndex, partLength);
			if (part === 'mm') monthStr = partValue;
			else if (part === 'yyyy') yearStr = partValue;
			tempIndex += partLength;
		}

		for (let i = 0; i < formatParts.length; i++) {
			const part = formatParts[i];
			const partLength = part.length;
			let partValue = numbers.substr(numberIndex, partLength);
			let digitsConsumed = partValue.length;

			if (part === 'mm' || part === 'dd') {
				if (
					numbers.length >= numberIndex + 3 &&
					numbers[numberIndex] === '0'
				) {
					const threeDigits = numbers.substr(numberIndex, 3);
					const candidateValue = threeDigits.substr(1, 2);
					const idx2 = digitIndices[numberIndex + 1];
					const idx3 = digitIndices[numberIndex + 2];
					const hasSeparator = value
						.substring(idx2 + 1, idx3)
						.includes(separator);

					if (!hasSeparator) {
						let isValid = false;
						const val = parseInt(candidateValue, 10);
						if (part === 'mm') {
							isValid = val >= 1 && val <= 12;
						} else if (part === 'dd') {
							const currentYear = yearStr
								? parseInt(yearStr, 10)
								: new Date().getFullYear();
							const currentMonth = monthStr
								? parseInt(monthStr, 10)
								: 0;
							const maxDays =
								currentMonth > 0
									? this.getMaxDaysInMonth(
											currentMonth,
											currentYear
										)
									: 31;
							isValid = val >= 1 && val <= maxDays;
						}

						if (isValid) {
							partValue = candidateValue;
							digitsConsumed = 3;
						}
					}
				}
			}

			if (!partValue) break;

			const remainingDigits =
				numbers.length - numberIndex - digitsConsumed;
			const hasMoreDigits = remainingDigits > 0;
			let constrainedValue = partValue;
			let partAutoPadded = false;

			if (part === 'mm') {
				const month = parseInt(partValue, 10);
				if (month > 12) {
					constrainedValue = '12';
				} else if (partValue.length === 1) {
					const currentDigitIndex = digitIndices[numberIndex];
					const nextChar = value[currentDigitIndex + 1];
					const hasSeparator = nextChar === separator;

					const isUnambiguous = month > 2;
					const autoPad =
						isUnambiguous && !isDeletion && !hasMoreDigits;
					const forcedPad = forcePad || hasSeparator;

					if ((autoPad || forcedPad) && month >= 1 && month <= 9) {
						constrainedValue = '0' + partValue;
						partAutoPadded = true;
					}
				}
				monthStr = constrainedValue;
			} else if (part === 'dd') {
				const day = parseInt(partValue, 10);
				const month = monthStr ? parseInt(monthStr, 10) : 0;
				const year = yearStr
					? parseInt(yearStr, 10)
					: new Date().getFullYear();
				const maxDays =
					month > 0 ? this.getMaxDaysInMonth(month, year) : 31;

				if (day > maxDays) {
					constrainedValue = String(maxDays).padStart(2, '0');
				} else if (day > 31) {
					constrainedValue = '31';
				} else if (partValue.length === 1) {
					const currentDigitIndex = digitIndices[numberIndex];
					const nextChar = value[currentDigitIndex + 1];
					const hasSeparator = nextChar === separator;

					const isUnambiguous = day > 3;
					const autoPad =
						isUnambiguous && !isDeletion && !hasMoreDigits;
					const forcedPad = forcePad || hasSeparator;

					if ((autoPad || forcedPad) && day >= 1 && day <= 9) {
						constrainedValue = '0' + partValue;
						partAutoPadded = true;
					}
				}
			} else if (part === 'yyyy') {
				constrainedValue = partValue;
				if (constrainedValue.length === 4) {
					yearStr = constrainedValue;
				}
			}

			masked += constrainedValue;
			numberIndex += digitsConsumed;
			if (partAutoPadded) isAutoPadded = true;

			if (
				i < formatParts.length - 1 &&
				constrainedValue.length === partLength
			) {
				masked += separator;
			}
		}

		return { masked, isAutoPadded };
	}

	private tryApplyStructurePreservingMask(
		value: string,
		separator: string,
		isDeletion: boolean
	): { masked: string; isAutoPadded: boolean } | null {
		const parts = value.split(separator);
		const formatParts = this.getFormatParts();

		// If structure is broken (different number of parts), fall back to global re-flow
		if (parts.length !== formatParts.length) {
			return null;
		}

		let maskedParts: string[] = [];
		let isAutoPadded = false;

		for (let i = 0; i < formatParts.length; i++) {
			const partFormat = formatParts[i];
			const rawPartValue = parts[i];
			const digits = rawPartValue.replace(/[^\d]/g, '');

			// Process this part specifically
			// We can reuse some logic from applyMask but scoped to just these digits
			let constrained = digits;
			let partAutoPadded = false;

			if (partFormat === 'mm') {
				// Special handling: if we have more than 2 digits, what do we do?
				// In structure preserving mode, we typically clamp or truncate
				// If user typed 123 in month, it's invalid.
				// But maybe they inserted: 1|2 -> 152.
				// Let's take the first 2 digits?
				let valStr = digits;
				if (valStr.length > 2) valStr = valStr.substring(0, 2);

				if (valStr) {
					const month = parseInt(valStr, 10);
					if (month > 12) {
						constrained = '12';
					} else {
						constrained = valStr;
						// Auto-pad logic for month
						// Only if single digit, unambiguous (>2), and NOT deletion (unless we are sure)
						// Actually, reusing the "relaxed" logic:
						if (valStr.length === 1) {
							if (month > 2 && !isDeletion) {
								constrained = '0' + valStr;
								partAutoPadded = true;
							}
						}
					}
				} else {
					constrained = ''; // empty part
				}
			} else if (partFormat === 'dd') {
				let valStr = digits;
				if (valStr.length > 2) valStr = valStr.substring(0, 2);

				if (valStr) {
					const day = parseInt(valStr, 10);
					// We need context for max days...
					// Ideal: get month/year from OTHER parts (which we are processing or have processed)
					// But for simplicity, let's use 31 max for intermediate edits or use best effort
					// We can look at maskedParts for 'mm' if it came before?
					// Usually mm comes before dd.

					// Find year and month indexes in format
					const mmIndex = formatParts.indexOf('mm');
					const yyyyIndex = formatParts.indexOf('yyyy');

					// If we already processed them, use maskedParts
					// If not, use raw parts (cleaned)
					const getPart = (idx: number) => {
						if (idx < i) return maskedParts[idx]; // processed
						return parts[idx].replace(/[^\d]/g, ''); // raw
					};

					const mStr = mmIndex !== -1 ? getPart(mmIndex) : '';
					const yStr = yyyyIndex !== -1 ? getPart(yyyyIndex) : '';

					const mVal = mStr ? parseInt(mStr, 10) : 0;
					const yVal = yStr
						? parseInt(yStr, 10)
						: new Date().getFullYear();
					const maxDays = this.getMaxDaysInMonth(mVal, yVal);

					if (day > maxDays) {
						constrained = String(maxDays).padStart(2, '0');
					} else if (day > 31) {
						constrained = '31';
					} else {
						constrained = valStr;
						// Auto-pad logic for day
						if (valStr.length === 1) {
							if (day > 3 && !isDeletion) {
								constrained = '0' + valStr;
								partAutoPadded = true;
							}
						}
					}
				} else {
					constrained = '';
				}
			} else if (partFormat === 'yyyy') {
				let valStr = digits;
				if (valStr.length > 4) valStr = valStr.substring(0, 4);
				constrained = valStr;
			}

			if (partAutoPadded) isAutoPadded = true;
			maskedParts.push(constrained);
		}

		return { masked: maskedParts.join(separator), isAutoPadded };
	}

	private getMaxDaysInMonth(month: number, year?: number): number {
		if (month < 1 || month > 12) return 31;
		const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
		if (month === 2) {
			const checkYear = year || new Date().getFullYear();
			const isLeapYear =
				(checkYear % 4 === 0 && checkYear % 100 !== 0) ||
				checkYear % 400 === 0;
			return isLeapYear ? 29 : 28;
		}
		return daysInMonth[month - 1];
	}

	private getFormatParts(): string[] {
		const separator = this.dateFormat.includes('/') ? '/' : '-';
		return this.dateFormat.split(separator);
	}

	private getMaxLength(): number {
		return this.dateFormat.length;
	}

	private getNewCursorPosition(
		oldValue: string,
		newValue: string,
		oldCursor: number,
		separator: string,
		isAutoPadded: boolean,
		isDeletion: boolean
	): number {
		if (newValue.length < oldValue.length) {
			return Math.min(oldCursor, newValue.length);
		}

		if (isAutoPadded) {
			if (newValue.endsWith(separator) && !oldValue.endsWith(separator)) {
				return newValue.length - separator.length;
			}
			return newValue.length;
		}

		const formatParts = this.getFormatParts();
		let position = 0;
		const fieldBoundaries: {
			start: number;
			end: number;
			partIndex: number;
		}[] = [];

		for (let i = 0; i < formatParts.length; i++) {
			const partLength = formatParts[i].length;
			const start = position;
			const end = position + partLength;
			fieldBoundaries.push({ start, end, partIndex: i });
			position = end;
			if (
				i < formatParts.length - 1 &&
				newValue.length > end &&
				newValue[end] === separator
			) {
				position += separator.length;
			}
		}

		if (newValue.length > oldValue.length) {
			let fieldIndex = -1;
			for (let i = 0; i < fieldBoundaries.length; i++) {
				const boundary = fieldBoundaries[i];
				if (
					oldCursor >= boundary.start &&
					oldCursor <= boundary.end + 1
				) {
					fieldIndex = i;
					break;
				}
			}

			if (fieldIndex >= 0 && fieldIndex < fieldBoundaries.length) {
				const currentBoundary = fieldBoundaries[fieldIndex];
				if (
					fieldIndex < formatParts.length - 1 &&
					newValue.length > currentBoundary.end &&
					newValue[currentBoundary.end] === separator
				) {
					return currentBoundary.end + separator.length;
				}
			}

			if (
				oldCursor < newValue.length &&
				newValue[oldCursor] === separator
			) {
				return oldCursor + 1;
			}
		}
		return Math.min(oldCursor, newValue.length);
	}
	private autoPadMonthAndDay(input: HTMLInputElement) {
		const value = input.value.trim();
		if (!value) return;

		const separator = this.dateFormat.includes('/') ? '/' : '-';
		const parts = value.split(separator);
		const formatParts = this.dateFormat.split(separator);

		// We need at least the month part to do anything meaningful for mm/dd/yyyy
		if (parts.length === 0) return;

		let changed = false;

		// Handle Month (mm)
		const monthIndex = formatParts.indexOf('mm');
		if (monthIndex !== -1 && maxParts(monthIndex)) {
			let monthPart = parts[monthIndex];
			if (monthPart.length === 1 && /^\d$/.test(monthPart)) {
				// Pad singular digit month
				parts[monthIndex] = '0' + monthPart;
				changed = true;
			} else if (
				monthPart.length === 2 &&
				monthPart !== '00' &&
				Number(monthPart) === 0
			) {
				// Prevent 00 or 0 from staying invalid if possible? No, mask handles validation usually
				// But we are interested in padding.
			}
		}

		// Handle Day (dd)
		const dayIndex = formatParts.indexOf('dd');
		if (dayIndex !== -1 && maxParts(dayIndex)) {
			let dayPart = parts[dayIndex];
			if (dayPart.length === 1 && /^\d$/.test(dayPart)) {
				// Pad singular digit day
				parts[dayIndex] = '0' + dayPart;
				changed = true;
			}
		}

		function maxParts(index: number) {
			// Ensure we don't pad if the user hasn't typed enough to be ambiguous?
			// The debounce handles the "waiting" part. If we are here, user stopped typing.
			// So if they left "2", it becomes "02".
			return parts.length > index;
		}

		if (changed) {
			const completedDate = parts.join(separator);
			if (completedDate !== value) {
				input.value = completedDate;
				this.previousValue.set(completedDate);
				input.dispatchEvent(new Event('input', { bubbles: true }));
			}
		}
	}

	private autoCompleteYear(input: HTMLInputElement) {
		const value = input.value.trim();
		if (!value) return;

		const separator = this.dateFormat.includes('/') ? '/' : '-';
		const parts = value
			.split(separator)
			.filter((part) => part.trim() !== '');
		const formatParts = this.dateFormat.split(separator);

		// Logic from component: check for 2 parts (month/day) or 3 parts (short year)

		let completedDate: string | null = null;

		// Case 1: 2 parts entered (assuming current year)
		if (parts.length === 2 && formatParts.length === 3) {
			const currentYear = new Date().getFullYear();
			const yearIndex = formatParts.indexOf('yyyy');

			const newParts = [...parts];
			if (yearIndex === 0) newParts.unshift(String(currentYear));
			else if (yearIndex === 1)
				newParts.splice(1, 0, String(currentYear));
			else newParts.push(String(currentYear));

			completedDate = newParts.join(separator);
		}
		// Case 2: 3 parts but year is 1-2 digits
		else if (parts.length === 3 && formatParts.length === 3) {
			const yearIndex = formatParts.indexOf('yyyy');
			if (yearIndex !== -1) {
				const yearPart = parts[yearIndex];
				if (yearPart.length > 0 && yearPart.length <= 2) {
					const yearNum = parseInt(yearPart, 10);
					const fullYear = 2000 + yearNum;
					parts[yearIndex] = String(fullYear);
					completedDate = parts.join(separator);
				}
			}
		}

		if (completedDate && completedDate !== value) {
			input.value = completedDate;
			this.previousValue.set(completedDate);
			// Dispatch input event to notify component/form
			input.dispatchEvent(new Event('input', { bubbles: true }));
		}
	}
}
